<template>
  <div class="ov-map">
    <!-- helle Insel (app-hell, tokens.css): Karte, Kompass und Namensnennung bleiben auch im Dunkelmodus hell -->
    <div ref="box" class="ov-map__box app-hell" :style="boxStyle">
      <!-- gedrehte Fläche: mittig im Kasten und so groß, dass beim Drehen keine Ecke leer bleibt -->
      <div class="ov-map__turn" :style="turnStyle">
        <l-map
          class="ov-map__leaflet"
          :options="mapOptions"
          :center="startCenter"
          :zoom="8"
          :no-blocking-animations="true"
          @ready="onReady"
        >
          <l-tile-layer :url="tileUrl" />
          <l-marker
            v-for="marker in markers"
            :key="marker.key"
            :lat-lng="marker.latLng"
            :icon="marker.icon"
            :z-index-offset="marker.zIndexOffset"
            :options="markerOptions"
          />
        </l-map>
      </div>

      <!-- Kompass oben rechts: die Nadel zeigt nach Norden (dreht mit der Karte) -->
      <span class="ov-map__compass" role="img" :aria-label="compassLabel" :title="compassLabel">
        <svg viewBox="0 0 24 24" width="22" height="22" :style="{ transform: 'rotate(' + turn.angle + 'deg)' }" aria-hidden="true">
          <path class="ov-map__needle-n" d="M12 1.5 L16 12 L8 12 Z" />
          <path class="ov-map__needle-s" d="M12 22.5 L8 12 L16 12 Z" />
          <circle class="ov-map__needle-pin" cx="12" cy="12" r="1.6" />
        </svg>
      </span>

      <!-- Namensnennung (Pflicht der OpenStreetMap-Lizenz): klein als „©“, voller Text bei Hover oder Fokus -->
      <span class="ov-map__attr">
        <template v-if="compact">© OpenStreetMap-Mitwirkende</template>
        <template v-else>© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap-Mitwirkende</a></template>
      </span>
    </div>
  </div>
</template>

<script>
// Live-Karte der Übersicht (Spezifikation 4.1.5, gen_r3.py:646–711) mit vue2-leaflet: statische Vorschau ohne
// Verschieben, Zoomen und Scrollrad (Ziel ist „Live-Tracking ›“).
// Ausschnitt (Kundenwunsch 12.10.2026): Die Karte wird so gedreht und gezoomt, dass möglichst viel zu sehen ist – die
// Hauptachse der Buspositionen (Hauptkomponente der Punkte in Mercator-Pixeln) liegt längs der langen Seite des Kastens,
// der Zoom füllt den Kasten mit Rand (höchstens 15, in Zehnteln). Gedreht wird nur, wenn es mindestens eine Viertel
// Zoomstufe bringt; kleine Winkel unter 5° entfallen. Technisch dreht CSS die ganze Leaflet-Fläche (die Karte ist
// unbedienbar, daher ohne Folgen für Leaflet), die Fläche ist dafür so groß, dass sie den Kasten in jeder Drehung
// bedeckt; die Marken drehen sich zurück und bleiben aufrecht. Oben rechts ein schlichter Kompass, dessen blaue
// Nadelhälfte (Navy) nach Norden zeigt.
// Je Bus ein L.divIcon (Klasse ov-bus, Fläche 44 × 44, busMark.js): weiße Marke mit feiner Kante, Liniennummer und
// Ampelpunkt ohne Zahl – grün bis 5 Min., gelb über 5 bis 10 Min., rot über 10 Min., grau ohne Wert (Legende im Fuß der
// Karte, HomePage). Seit 13.10.2026 pulsiert der Punkt, solange die letzte Position höchstens 3 Minuten alt ist; sonst
// steht dort ein Blitz mit Schrägstrich in der Ampelfarbe. Ein Takt alle 30 Sekunden hält das ohne neue Daten aktuell.
// Verspätete liegen oben. Untergrund farbig wie OpenStreetMap; die Namensnennung unten rechts als kleines „©“.
// Die Marken sind nur Anzeige: kein Hinweis beim Darüberfahren oder Antippen (Kundenwunsch 12.10.2026); Details zeigt
// „Live-Tracking ›“.
// Freitexte aus der GPS-Antwort (comment, username) kommen nie ins HTML. Die Karte füllt ihren Behälter (Mindesthöhe
// height); ein ResizeObserver dreht und passt nach Größenänderungen neu ein.
import L from 'leaflet';
import { LMap, LTileLayer, LMarker } from 'vue2-leaflet';
import { delayLevel } from './overviewData';
import { busMarkHtml } from './busMark';

const MAP_OPTIONS = Object.freeze({
  zoomControl: false,
  attributionControl: false,
  scrollWheelZoom: false,
  doubleClickZoom: false,
  boxZoom: false,
  keyboard: false,
  dragging: false,
  touchZoom: false,
  // Zoom in Zehnteln, damit der Ausschnitt den Kasten füllt
  zoomSnap: 0.1
});
const START_CENTER = Object.freeze([48.9853399, 12.9804694]);
const MARKER_OPTIONS = Object.freeze({ keyboard: false, interactive: false });
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
// Rand um die Positionen im Kasten (die Marken sind bis rund 60 breit und 26 hoch)
const PAD_X = 40;
const PAD_Y = 28;
const MAX_ZOOM = 15;
const MIN_ZOOM = 4;
// Bezugszoom für die Pixelrechnung
const REF_ZOOM = 15;

// Takt für „Position höchstens 3 Minuten alt“ (das Dashboard lädt GPS nicht von selbst neu)
const NOW_TICK = 30000;

// Punkt um den Ursprung drehen (Bildschirmkoordinaten, y nach unten; positiver Winkel = im Uhrzeigersinn wie CSS)
function rotate(p, rad) {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c };
}

// Winkel in (−90°, 90°]
function normalize(deg) {
  let d = deg % 180;
  if (d > 90) {
    d -= 180;
  }
  if (d <= -90) {
    d += 180;
  }
  return d;
}

// Ausschnitt für Drehung deg: Zoom, der alle Punkte mit Rand in den Kasten W × H bringt, und die Mitte in Pixeln (REF_ZOOM)
function fitFor(points, deg, W, H) {
  const rad = deg * Math.PI / 180;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  points.forEach((p) => {
    const q = rotate(p, rad);
    minX = Math.min(minX, q.x);
    maxX = Math.max(maxX, q.x);
    minY = Math.min(minY, q.y);
    maxY = Math.max(maxY, q.y);
  });
  const w = maxX - minX;
  const h = maxY - minY;
  const availW = Math.max(W - 2 * PAD_X, 40);
  const availH = Math.max(H - 2 * PAD_Y, 40);
  let zoom;
  if (w < 1 && h < 1) {
    zoom = 14;
  } else {
    const scale = Math.min(availW / Math.max(w, 1), availH / Math.max(h, 1));
    zoom = REF_ZOOM + Math.log2(scale);
  }
  zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.floor(zoom * 10) / 10));
  const center = rotate({ x: (minX + maxX) / 2, y: (minY + maxY) / 2 }, -rad);
  return { zoom, center };
}

export default {
  name: 'OvLiveMap',
  components: { LMap, LTileLayer, LMarker },
  props: {
    // Zeilen aus gps?customer_id=…&period=20 (latitude, longitude, line_id, last_delay, time)
    buses: { type: Array, default: () => [] },
    // Kartenhöhe 240 (Tablet hoch 280, Handy 210)
    height: { type: Number, default: 240 },
    // Handy: Namensnennung ohne Link (die ganze Karte ist ein Link)
    compact: { type: Boolean, default: false }
  },
  data() {
    return {
      mapOptions: MAP_OPTIONS,
      startCenter: START_CENTER,
      tileUrl: TILE_URL,
      now: Date.now(),
      // gedrehte Fläche: Breite, Höhe (px) und Winkel (Grad, im Uhrzeigersinn)
      turn: { w: 0, h: 0, angle: 0 }
    };
  },
  computed: {
    // Marker nur Anzeige: ohne Tastaturfokus und nicht interaktiv (auf dem Handy gehört Antippen dem Kartenlink)
    markerOptions() {
      return MARKER_OPTIONS;
    },
    boxStyle() {
      return { minHeight: this.height + 'px' };
    },
    turnStyle() {
      if (!this.turn.w || !this.turn.h) {
        return { inset: 0 };
      }
      return {
        width: this.turn.w + 'px',
        height: this.turn.h + 'px',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%) rotate(' + this.turn.angle + 'deg)',
        '--ov-turn': this.turn.angle + 'deg'
      };
    },
    compassLabel() {
      const a = Math.round(this.turn.angle);
      return a ? 'Kompass: Karte um ' + Math.abs(a) + '° ' + (a > 0 ? 'im' : 'gegen den') + ' Uhrzeigersinn gedreht' : 'Kompass: Norden oben';
    },
    positions() {
      return this.buses.filter(row => row && isFinite(parseFloat(row.latitude)) && isFinite(parseFloat(row.longitude)));
    },
    // L.divIcon je Datenstand und Takt; Text nur aus line_id (maskiert, busMark.js)
    markers() {
      return this.positions.map((row, i) => {
        const level = delayLevel(row);
        const icon = L.divIcon({
          className: 'ov-bus',
          html: busMarkHtml(row, { now: this.now }),
          iconSize: [44, 44],
          iconAnchor: [22, 22]
        });
        return Object.freeze({
          key: i + '-' + parseFloat(row.latitude) + '-' + parseFloat(row.longitude),
          latLng: Object.freeze([parseFloat(row.latitude), parseFloat(row.longitude)]),
          icon,
          zIndexOffset: level === 'late' ? 1000 : (level === 'warn' ? 500 : 0)
        });
      });
    }
  },
  watch: {
    positions() {
      this.scheduleRefit();
    }
  },
  mounted() {
    this.nowTimer = window.setInterval(() => {
      this.now = Date.now();
    }, NOW_TICK);
    if (typeof window.ResizeObserver === 'function') {
      this.observer = new window.ResizeObserver(this.scheduleRefit);
      this.observer.observe(this.$refs.box);
    } else {
      window.addEventListener('resize', this.scheduleRefit);
    }
  },
  beforeDestroy() {
    window.clearInterval(this.nowTimer);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    } else {
      window.removeEventListener('resize', this.scheduleRefit);
    }
    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    this.map = null;
  },
  methods: {
    onReady(map) {
      // Leaflet-Instanz nicht reaktiv ablegen
      this.map = map;
      this.scheduleRefit();
    },
    scheduleRefit() {
      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
      }
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.refit();
      });
    },
    // Drehung und Ausschnitt für den aktuellen Kasten bestimmen, Fläche anpassen, dann einpassen
    refit() {
      const box = this.$refs.box;
      if (!this.map || !box || !box.clientWidth || !box.clientHeight) {
        return;
      }
      const W = box.clientWidth;
      const H = box.clientHeight;
      const points = this.positions.map(row => L.CRS.EPSG3857.latLngToPoint(
        L.latLng(parseFloat(row.latitude), parseFloat(row.longitude)), REF_ZOOM));
      const angle = this.bestAngle(points, W, H);
      const rad = Math.abs(angle) * Math.PI / 180;
      const w = Math.ceil(W * Math.cos(rad) + H * Math.sin(rad)) + 2;
      const h = Math.ceil(W * Math.sin(rad) + H * Math.cos(rad)) + 2;
      this.turn = { w, h, angle };
      this.$nextTick(() => {
        if (!this.map) {
          return;
        }
        this.map.invalidateSize(false);
        if (!points.length) {
          return;
        }
        const fit = fitFor(points, angle, W, H);
        const center = L.CRS.EPSG3857.pointToLatLng(L.point(fit.center.x, fit.center.y), REF_ZOOM);
        this.map.setView(center, fit.zoom, { animate: false });
      });
    },
    // Winkel, der die Hauptachse der Punkte auf die lange Seite des Kastens legt; 0, wenn es weniger als eine Viertel
    // Zoomstufe bringt oder der Winkel unter 5° liegt
    bestAngle(points, W, H) {
      if (points.length < 2) {
        return 0;
      }
      let mx = 0;
      let my = 0;
      points.forEach((p) => {
        mx += p.x;
        my += p.y;
      });
      mx /= points.length;
      my /= points.length;
      let sxx = 0;
      let syy = 0;
      let sxy = 0;
      points.forEach((p) => {
        const dx = p.x - mx;
        const dy = p.y - my;
        sxx += dx * dx;
        syy += dy * dy;
        sxy += dx * dy;
      });
      if (sxx + syy < 1) {
        return 0;
      }
      const axis = 0.5 * Math.atan2(2 * sxy, sxx - syy) * 180 / Math.PI;
      const candidate = normalize(W >= H ? -axis : 90 - axis);
      if (Math.abs(candidate) < 5) {
        return 0;
      }
      const straight = fitFor(points, 0, W, H).zoom;
      const turned = fitFor(points, candidate, W, H).zoom;
      return turned - straight >= 0.25 ? Math.round(candidate * 10) / 10 : 0;
    }
  }
};
</script>

<style scoped>
.ov-map {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.ov-map__box {
  position: relative;
  z-index: 0;
  flex: 1 1 auto;
  overflow: hidden;
  border-radius: var(--r-ctl);
  background: var(--map-bg);
}
/* Gedrehte Fläche: absolut (der Kasten hat nur eine Mindesthöhe), Größe und Drehung setzt turnStyle */
.ov-map__turn {
  position: absolute;
  transform-origin: 50% 50%;
}
.ov-map__turn .ov-map__leaflet {
  position: absolute;
  inset: 0;
}
.ov-map__box ::v-deep .leaflet-container {
  background: var(--map-bg);
  font-family: var(--font);
}

/* Kompass: runder Knopf-Look wie der WLAN-Status (Kante --edge, --sh-ctl); schlichte, schmale Nadel – Nordhälfte im
   Navy des Farbkonzepts (--link), Südhälfte hellgrau (Kundenwunsch 12.10.2026) */
.ov-map__compass {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
  width: 34px;
  height: 34px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--edge);
  border-radius: 50%;
  background: var(--surface-raised);
  box-shadow: var(--sh-ctl);
  pointer-events: auto;
}
.ov-map__compass svg {
  display: block;
  transition: transform .3s ease;
}
/* deutlich: breitere Nadel, Nord kräftiges Navy, Süd mittleres Grau, weißer Drehpunkt */
.ov-map__needle-n {
  fill: var(--link);
}
.ov-map__needle-s {
  fill: var(--ink-4);
}
.ov-map__needle-pin {
  fill: var(--surface-raised);
}

/* Namensnennung klein als „©“-Chip; der volle Text klappt bei Hover oder Fokus auf */
.ov-map__attr {
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 3;
  max-width: 20px;
  height: 20px;
  box-sizing: border-box;
  padding: 0 0 0 5px;
  overflow: hidden;
  border-radius: 10px;
  background: rgba(255, 255, 255, .92);
  box-shadow: 0 0 0 1px rgba(21, 26, 33, .08), 0 1px 2px rgba(21, 26, 33, .08);
  font-size: 11px;
  line-height: 20px;
  white-space: nowrap;
  color: var(--ink-3);
  transition: max-width .2s ease, padding .2s ease;
}
.ov-map__attr:hover,
.ov-map__attr:focus-within {
  max-width: 260px;
  padding: 0 8px 0 6px;
}
.ov-map__attr a {
  color: inherit;
}

@media (prefers-reduced-motion: reduce) {
  .ov-map__compass svg,
  .ov-map__attr {
    transition: none;
  }
}
</style>

